"use client";

import Image from "next/image";
import styles from "./listing-card.module.css";
import StatusLabel from "./status-label";
import {
  MdArrowBackIosNew,
  MdArrowForwardIos,
  MdBathtub,
  MdBed,
  MdEventAvailable,
  MdFavorite,
  MdFavoriteBorder,
  MdMeetingRoom,
  MdOutlineTram,
  MdSquareFoot,
  MdSubway,
} from "react-icons/md";
import { FaBus, FaHospital, FaLocationDot } from "react-icons/fa6";
import { IoChatboxEllipses } from "react-icons/io5";
import {
  ApiError,
  ContactDialogType,
  Currencies,
  ListingCardData,
  ListingTypeEnum,
  TypeEnum,
} from "@/types/types";
import { useLocale, useTranslations } from "next-intl";
import { useUserAuthDataContext } from "@/global-context/user-auth-context";
import { m2ToSqft } from "@/lib/sqftToM2";
import { useCurrenciesContext } from "@/global-context/currencies-context";
import { formatPrice } from "@/lib/price-formatter";
import { Swiper, SwiperSlide, useSwiper } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import { Pagination } from "swiper/modules";
import { useRouter } from "@/i18n/navigation";
import { FaSchool } from "react-icons/fa";
import { PiParkFill } from "react-icons/pi";
import { IoIosFitness } from "react-icons/io";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useListingsCacheStore } from "@/global-cache/useListingsCacheStore";
import { motion } from "framer-motion";
import { useAnimationStore } from "./useHasBeenAnimatedStore";
import { env } from "@/lib/env";
import { makeLocalizedHrefNoLocale } from "@/lib/localizedRoutes/routeHelpers";
import ContactDialog from "../contact-agent-info/seller-contact-dialog";
import { resolveCardSavedError } from "@/lib/resolve-listing-card-saved-state";
import { useScrollLock } from "@/hooks/use-scroll-lock";
import { getMedia } from "@/lib/media/shared";
import { formatAvailableFrom } from "@/lib/formatAvailableFrom";
import { useOptimalImageSize } from "@/hooks/useOptimalImageSize";

const LISTINGSENDPOINTURL = env.NEXT_LISTINGS_API_URL as string;

export default function GeneralListingCard({
  data,
  isResizable,
  hasActions,
  hasRoundButtons,
  hasStatusLabel = false,
  hasTypeLabel,
  statusLabelText,
  hasPreviewButton,
  animationId,
}: {
  data: ListingCardData;
  isResizable: boolean;
  hasActions: boolean;
  hasRoundButtons: boolean;
  hasStatusLabel: boolean;
  hasTypeLabel?: boolean;
  statusLabelText?: string;
  hasPreviewButton?: PreviewButtonParam;
  animationId?: string;
}) {
  const { userData } = useUserAuthDataContext();
  const { currenciesRates } = useCurrenciesContext();
  const currencyKey = `EUR-${userData.currency}` as keyof Currencies;
  const rate: number = currenciesRates[currencyKey];
  const t = useTranslations("ListingCard");
  const router = useRouter();
  const isSaved = useListingsCacheStore(
    (state) => state.meta[data.listing_id]?.is_saved,
  );
  const updateSavedState = useListingsCacheStore(
    (state) => state.updateSavedState,
  );
  const { animatedIds, markAnimated } = useAnimationStore();
  const hasBeenAnimated = animatedIds.has(animationId ?? data.listing_id);
  const locale = useLocale();
  const [isContactOpen, setIsContactOpen] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const [isPriceRequest, setIsPriceRequest] = useState(false);
  const optimalImageSize = useOptimalImageSize();

  useScrollLock(isContactOpen);

  const listingUrl = makeLocalizedHrefNoLocale(locale, "listing", [
    `${data.listing_id}`,
  ]);
  const handleCardClick = () => {
    router.push(listingUrl);
  };

  const scrollRef = useRef<HTMLUListElement>(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);
  const hasMoved = useRef(false);
  const pointerDownCoords = useRef<{ x: number; y: number } | null>(null);
  const isDraggingCard = useRef(false);

  useEffect(() => {
    const slider = scrollRef.current;
    if (!slider) return;

    const handlePointerDown = (e: PointerEvent) => {
      e.stopPropagation();
      isDragging.current = true;
      startX.current = e.pageX;
      scrollLeft.current = slider.scrollLeft;
      hasMoved.current = false;
      slider.setPointerCapture(e.pointerId);
    };

    const handlePointerMove = (e: PointerEvent) => {
      if (!isDragging.current) return;
      e.stopPropagation();
      const x = e.pageX;
      const walk = x - startX.current;
      if (Math.abs(walk) > 1) hasMoved.current = true;
      slider.scrollLeft = scrollLeft.current - walk;
    };

    const handlePointerUp = (e: PointerEvent) => {
      e.stopPropagation();
      isDragging.current = false;
      slider.releasePointerCapture(e.pointerId);
    };

    const handleClickCapture = (e: MouseEvent) => {
      if (hasMoved.current) {
        e.stopPropagation();
        e.preventDefault();
      }
    };

    slider.addEventListener("pointerdown", handlePointerDown);
    slider.addEventListener("pointermove", handlePointerMove);
    slider.addEventListener("pointerup", handlePointerUp);
    slider.addEventListener("click", handleClickCapture, true); // capture phase

    return () => {
      slider.removeEventListener("pointerdown", handlePointerDown);
      slider.removeEventListener("pointermove", handlePointerMove);
      slider.removeEventListener("pointerup", handlePointerUp);
      slider.removeEventListener("click", handleClickCapture, true);
    };
  }, []);

  const handleCardPointerDown = (e: React.PointerEvent) => {
    pointerDownCoords.current = { x: e.clientX, y: e.clientY };
    isDraggingCard.current = false;
  };

  const handleCardPointerUp = (e: React.PointerEvent) => {
    if (pointerDownCoords.current) {
      const dx = Math.abs(e.clientX - pointerDownCoords.current.x);
      const dy = Math.abs(e.clientY - pointerDownCoords.current.y);

      if (dx > 10 || dy > 10) {
        isDraggingCard.current = true;
      }
    }
  };

  const handleSmartClick = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (isDraggingCard.current) {
      isDraggingCard.current = false;
      return;
    }

    handleCardClick(); // Your existing router.push logic
  };

  const { previewPhotos, totalPhotosCount } = useMemo(() => {
    const allPhotos = Object.entries(data.photos || {})
      .sort((a, b) => Number(a[0]) - Number(b[0]))
      .map(([_, item]) => item);

    const total = allPhotos.length;
    const cappedPhotos = allPhotos.slice(0, 10);

    const resolved = cappedPhotos.map((photo) => {
      const resolvedMedia = getMedia(photo, optimalImageSize);
      const displaySrc =
        resolvedMedia?.src || (photo as any).localUrl || "/broken_image.webp";
      const displayWebp = resolvedMedia?.webp;

      return {
        ...photo,
        resolvedMedia,
        displaySrc,
        displayWebp,
      };
    });

    return { previewPhotos: resolved, totalPhotosCount: total };
  }, [data.photos, optimalImageSize]);

  return (
    <motion.article
      initial={!hasBeenAnimated ? { opacity: 0 } : false}
      animate={!hasBeenAnimated ? { opacity: 1 } : false}
      transition={{
        duration: 0.3,
        delay: 0.0,
        ease: "easeIn",
      }}
      onAnimationComplete={() => {
        if (!hasBeenAnimated) markAnimated(animationId ?? data.listing_id);
      }}
    >
      <a
        href={listingUrl}
        draggable={false}
        className={`${styles.cardWrapper} ${
          hasActions && styles.actionsLayout
        } ${isResizable && styles.resizable} ${
          data.listing_type === ListingTypeEnum.SPOTLIGHT && styles.spotlight
        }`}
        role="link"
        tabIndex={0}
        onPointerDown={handleCardPointerDown}
        onPointerUp={handleCardPointerUp}
        onClick={handleSmartClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            handleSmartClick(e);
          }
        }}
      >
        {hasTypeLabel && (
          <div
            className={`${styles.typeLabel} ${
              hasTypeLabel && styles[data.listing_type]
            }`}
          >
            {LISTING_TYPES[data.listing_type]}
          </div>
        )}
        <figure className={styles.imageCarousel} aria-label={t("photoGallery")}>
          {data.available_from && (
            <div className={styles.floatingAvailableBadge}>
              <MdEventAvailable />
              <span>
                {t("from")} {formatAvailableFrom(data.available_from, locale)}
              </span>
            </div>
          )}
          <Swiper
            loop={false}
            resistance={true}
            resistanceRatio={0}
            spaceBetween={0}
            slidesPerView={1}
            modules={[Pagination]}
            pagination={{ clickable: true, dynamicBullets: true }}
            className={styles.swiperComponent}
          >
            {previewPhotos.length > 0 ? (
              previewPhotos.map((photoItem, index) => {
                const isLastCappedSlide = index === 9 && totalPhotosCount > 10;

                return (
                  <SwiperSlide
                    key={photoItem.MediaId || index}
                    className={styles.swiperSlideComponent}
                    role="group"
                    aria-roledescription="slide"
                    aria-label={`${t("image")} ${index + 1} ${t("of")} ${previewPhotos.length}`}
                  >
                    <div className={styles.imageContainer}>
                      <picture style={{ display: "contents" }}>
                        {photoItem.displayWebp && (
                          <source
                            srcSet={photoItem.displayWebp}
                            type="image/webp"
                          />
                        )}
                        <Image
                          loading={index !== 0 ? "lazy" : undefined}
                          className={styles.image}
                          src={photoItem.displaySrc}
                          width={1200}
                          height={675}
                          unoptimized={true}
                          alt={`${t("propertyPhoto")} ${index + 1}`}
                          draggable={false}
                          priority={index === 0}
                          placeholder="blur"
                          blurDataURL="/placeholder_image.jpg"
                          fetchPriority={index === 0 ? "high" : "auto"}
                        />
                      </picture>

                      {isLastCappedSlide && (
                        <div
                          className={styles.seeAllOverlay}
                          onClick={handleSmartClick}
                        >
                          <div className={styles.overlayContent}>
                            <span className={styles.overlayPlus}>
                              +{totalPhotosCount - 10}
                            </span>
                            <span className={styles.overlayText}>
                              {t("seeAllPhotos") || "See All Photos"}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </SwiperSlide>
                );
              })
            ) : (
              <SwiperSlide className={styles.swiperSlideComponent}>
                <Image
                  className={styles.image}
                  src="/broken_image.webp"
                  width={1200}
                  height={675}
                  unoptimized={true}
                  alt="No photos available"
                  draggable={false}
                />
              </SwiperSlide>
            )}
            <NextAndBackButtons />
          </Swiper>
        </figure>
        <div className={styles.contentWrapper}>
          <div className={styles.topContent}>
            <header>
              <h2 className={styles.title}>{data.title}</h2>
            </header>
            <div className={styles.detailsRow}>
              <dl className={styles.details}>
                <dt className={styles.srOnly}>{t("bedrooms")}</dt>
                <dd className={styles.fullText}>
                  <MdBed aria-hidden="true" className={styles.icon} />
                  <span>
                    {data.number_of_bedrooms || "-"} {t("bedrooms")}
                  </span>
                </dd>
              </dl>
              <div className={styles.separator}></div>
              <dl className={styles.details}>
                <dt className={styles.srOnly}>{t("bathrooms")}</dt>
                <dd className={styles.fullText}>
                  <MdBathtub aria-hidden="true" className={styles.icon} />
                  <span>
                    {data.number_of_bathrooms || "-"} {t("bathrooms")}
                  </span>
                </dd>
              </dl>
            </div>
            <div className={styles.detailsRow}>
              <dl className={styles.details}>
                <dt className={styles.srOnly}>{t("rooms")}</dt>
                <dd className={styles.fullText}>
                  <MdMeetingRoom aria-hidden="true" className={styles.icon} />
                  <span>
                    {data?.number_of_rooms || "-"} {t("rooms")}
                  </span>
                </dd>
              </dl>
              <div className={styles.separator}></div>
              <dl className={styles.details}>
                <dt className={styles.srOnly}>{t("area")}</dt>
                <dd className={styles.fullText}>
                  <MdSquareFoot aria-hidden="true" className={styles.icon} />
                  {data.type !== TypeEnum.LAND ? (
                    <span>
                      {userData.unit_system === "Metric"
                        ? data.interior_space
                        : m2ToSqft(data.interior_space) || "-"}{" "}
                      {userData.unit_system === "Metric" ? "sqm" : "sqft"}
                    </span>
                  ) : (
                    <span>
                      {userData.unit_system === "Metric"
                        ? data.lot_size
                        : m2ToSqft(data.lot_size) || "-"}{" "}
                      {userData.unit_system === "Metric" ? "sqm" : "sqft"}
                    </span>
                  )}
                </dd>
              </dl>
            </div>
            <address className={styles.location}>
              <FaLocationDot className={styles.icon} aria-hidden="true" />
              <span className={styles.address}>{data.address}</span>
            </address>
          </div>
          <div
            className={`${styles.bottomWrapper} ${
              hasRoundButtons && styles.hasRoundButtons
            }`}
          >
            <span
              className={styles.price}
              aria-label={
                data.price_on_request
                  ? t("findPrice") // Make sure to add this to your translation files!
                  : `${t("price")} ${formatPrice(rate * data.price)} ${userData.currency}${
                      data?.purpose === "for-rent"
                        ? data.is_price_per_year
                          ? ` per year`
                          : ` per month`
                        : ""
                    }`
              }
            >
              {data.price_on_request ? (
                <button
                  className={styles.findPriceButton}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsPriceRequest(true);
                    setIsContactOpen(true);
                  }}
                >
                  {t("findPrice")}
                </button>
              ) : (
                <>
                  <strong>
                    {t("price")} {formatPrice(rate * data.price)}{" "}
                    {userData.currency}{" "}
                  </strong>
                  {data?.purpose === "for-rent" &&
                    (data.is_price_per_year
                      ? `/ ${t("year")}`
                      : `/ ${t("month")}`)}
                </>
              )}
            </span>
            {hasStatusLabel && (
              <div className={styles.statusLabelWrapper}>
                <StatusLabel text={statusLabelText} />
              </div>
            )}
            {hasPreviewButton?.hasButton && (
              <button
                className={styles.modifyButton}
                onKeyDown={(e) => {
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  hasPreviewButton.onClick();
                }}
              >
                {t("preview")}
              </button>
            )}
            {hasRoundButtons && (
              <div className={styles.actionButtons}>
                <button
                  aria-label={
                    isSaved ? t("removeFromFavorites") : t("addToFavorites")
                  }
                  onKeyDown={(e) => {
                    e.stopPropagation();
                  }}
                  onClick={async (e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    if (userData.idToken === null) {
                      toast(
                        "Please reauthenticate before performing this action.",
                        {
                          type: "error",
                        },
                      );
                      return;
                    }
                    if (isSaved) {
                      updateSavedState(data.listing_id, false);
                      try {
                        const response = await fetch(
                          `${LISTINGSENDPOINTURL}/removeFromSavedListings`,
                          {
                            method: "POST",
                            headers: {
                              "Content-Type": "application/json",
                              Authorization: userData.idToken,
                            },
                            body: JSON.stringify({
                              listing_id: data.listing_id,
                            }),
                          },
                        );
                        if (response.ok) {
                          return;
                        } else {
                          const apiErr = (await response.json()) as ApiError;
                          resolveCardSavedError({
                            apiErr: apiErr,
                            listingId: data.listing_id,
                            attemptedState: false,
                            updateSavedState: updateSavedState,
                          });
                          throw new Error(apiErr.message);
                        }
                      } catch (err) {
                        const errorMessage =
                          err instanceof Error
                            ? err.message
                            : "An unexpected error occurred.";
                        toast(
                          errorMessage === "Failed to fetch"
                            ? "Unable to save this listing to database."
                            : errorMessage,
                          {
                            type: "error",
                          },
                        );
                        return;
                      }
                    } else {
                      updateSavedState(data.listing_id, true);
                      try {
                        const response = await fetch(
                          `${LISTINGSENDPOINTURL}/addToSavedListings`,
                          {
                            method: "POST",
                            headers: {
                              "Content-Type": "application/json",
                              Authorization: userData.idToken,
                            },
                            body: JSON.stringify({
                              listing_id: data.listing_id,
                            }),
                          },
                        );
                        if (response.ok) {
                          return;
                        } else {
                          const apiErr = (await response.json()) as ApiError;
                          resolveCardSavedError({
                            apiErr: apiErr,
                            listingId: data.listing_id,
                            attemptedState: true,
                            updateSavedState: updateSavedState,
                          });
                          throw new Error(apiErr.message);
                        }
                      } catch (err) {
                        const errorMessage =
                          err instanceof Error
                            ? err.message
                            : "An unexpected error occurred.";
                        toast(
                          errorMessage === "Failed to fetch"
                            ? "Unable to unsave this listing from database."
                            : errorMessage,
                          {
                            type: "error",
                          },
                        );
                        return;
                      }
                    }
                  }}
                  className={styles.roundButton}
                >
                  {isSaved ? (
                    <MdFavorite
                      className={`${styles.icon} ${styles.heartActive}`}
                    />
                  ) : (
                    <MdFavoriteBorder className={styles.icon} />
                  )}
                </button>
                <button
                  aria-label={t("sendMessageToUser", {
                    userName: data.creator.name,
                  })}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setIsPriceRequest(false);
                      setIsContactOpen(true);
                    }
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setIsPriceRequest(false);
                    setIsContactOpen(true);
                  }}
                  className={styles.roundButton}
                >
                  <IoChatboxEllipses className={styles.icon} />
                </button>
              </div>
            )}
          </div>
        </div>
      </a>
      {isContactOpen && (
        <ContactDialog
          type={ContactDialogType.LISTING}
          userSub={data.created_by}
          userType={data.creator.user_type}
          photoUrl={data.creator.photo_url}
          userName={data.creator.name}
          listingId={data.listing_id}
          listingTitle={data.title}
          closeCallback={() => {
            setIsPriceRequest(false);
            setIsContactOpen(false);
          }}
          phoneNumber={data.creator.phone_number}
          hasWhatsAppActive={data.creator.has_whatsapp_active}
          dialogRef={dialogRef}
          isPriceRequest={isPriceRequest}
        />
      )}
    </motion.article>
  );
}

export function ListingCardLoading({
  isResizable,
  hasActions,
  hasRoundButtons,
  hasStatusLabel,
  hasModifyButton,
}: {
  isResizable: boolean;
  hasActions: boolean;
  hasRoundButtons: boolean;
  hasStatusLabel: boolean;
  hasModifyButton: boolean;
}) {
  return (
    <div className={styles.loadingWrapper}>
      <div
        className={`${styles.cardWrapper} ${
          hasActions && styles.actionsLayout
        } ${isResizable && styles.resizable} ${styles.isCardLoading}`}
      >
        <div className={`${styles.imageCarousel} ${styles.isLoading}`}></div>
        <div
          className={`${styles.contentWrapper} ${styles.contentWrapperLoading}`}
        >
          <div className={styles.topContent}>
            <h2 className={`${styles.title} ${styles.isLoading}`}></h2>
            {isResizable && (
              <h2
                className={`${styles.title} ${styles.isLoading} ${styles.bigLayout}`}
                style={{ width: "60%" }}
              ></h2>
            )}
            <span className={styles.detailsRow}>
              <span className={`${styles.details} ${styles.isLoading}`}></span>
              <div className={styles.separator}></div>
              <span className={`${styles.details} ${styles.isLoading}`}></span>
            </span>
            <span className={styles.detailsRow}>
              <span className={`${styles.details} ${styles.isLoading}`}></span>
              <div className={styles.separator}></div>
              <span className={`${styles.details} ${styles.isLoading}`}></span>
            </span>
            {/* <ul className={styles.proximityWrapper}>
              {[...Array(5)].map((_, i) => {
                return (
                  <li
                    key={i}
                    className={`${styles.proximityPill} ${styles.isLoading}`}
                  ></li>
                );
              })}
            </ul> */}
            <span className={styles.location}>
              <span className={styles.isLoading}></span>
            </span>
          </div>
          <div
            className={`${styles.bottomWrapper} ${
              hasRoundButtons && styles.hasRoundButtons
            }`}
          >
            <span className={`${styles.price} ${styles.isLoading}`}></span>
            {hasStatusLabel && (
              <div
                className={`${styles.statusLabelWrapper} ${styles.isLoading}`}
              ></div>
            )}
            {hasModifyButton && (
              <button
                className={`${styles.modifyButton} ${styles.isLoading}`}
              ></button>
            )}
            {hasRoundButtons && (
              <div className={styles.actionButtons}>
                <button
                  className={`${styles.roundButton} ${styles.isLoading}`}
                ></button>
                <button
                  className={`${styles.roundButton} ${styles.isLoading}`}
                ></button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export interface PreviewButtonParam {
  hasButton: boolean;
  onClick: () => void;
}

function NextAndBackButtons() {
  const swiper = useSwiper();
  const [isBeginning, setIsBeginning] = useState(swiper.isBeginning);
  const [isEnd, setIsEnd] = useState(swiper.isEnd);

  useEffect(() => {
    // Listen to swiper events to update our button visibility state
    const updateNavigation = () => {
      setIsBeginning(swiper.isBeginning);
      setIsEnd(swiper.isEnd);
    };

    swiper.on("slideChange", updateNavigation);

    // Cleanup listener on unmount
    return () => {
      swiper.off("slideChange", updateNavigation);
    };
  }, [swiper]);

  return (
    <>
      {!isBeginning && (
        <button
          className={styles.backButton}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            swiper.slidePrev();
          }}
        >
          <MdArrowBackIosNew />
        </button>
      )}
      {!isEnd && (
        <button
          className={styles.nextButton}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            swiper.slideNext();
          }}
        >
          <MdArrowForwardIos />
        </button>
      )}
    </>
  );
}

const LISTING_TYPES = {
  essential: "Essential",
  plus: "Plus",
  prime: "Prime",
  spotlight: "Spotlight",
};

// "school":                   "school",
// "underground_train-subway": "subwayStation",
// "hospital":                 "hospital",
// "bus_stop":                 "busStation",
// "fitness-health_club":      "fitnessClub",
// "park-recreation_area":     "park",
// "lightrail":                "lightRailStation",

{
  /* {!data.proximity_places ||
            Object.keys(data.proximity_places).length === 0 ? (
              <ul className={styles.proximityWrapper}></ul>
            ) : (
              <ul ref={scrollRef} className={styles.proximityWrapper}>
                {Object.entries(data.proximity_places)
                  .sort(([, a], [, b]) => a.distance - b.distance)
                  .map(([key, item], i) => {
                    return (
                      <li
                        key={i}
                        className={`${styles.proximityPill} ${
                          item.type === "hospital" && styles.colorRed
                        } ${
                          item.type === "subwayStation" && styles.colorBlue
                        } ${item.type === "park" && styles.colorGreen} ${
                          item.type === "school" && styles.colorGray
                        } ${
                          item.type === "fitnessClub" && styles.colorOrange
                        } ${
                          item.type === "lightRailStation" && styles.colorPurple
                        } ${item.type === "busStation" && styles.colorYellow} `}
                      >
                        {item.type === "school" && <FaSchool />}
                        {item.type === "subwayStation" && <MdSubway />}
                        {item.type === "hospital" && <FaHospital />}
                        {item.type === "busStation" && <FaBus />}
                        {item.type === "fitnessClub" && <IoIosFitness />}
                        {item.type === "park" && <PiParkFill />}
                        {item.type === "lightRailStation" && <MdOutlineTram />}
                        {item.distance} m
                      </li>
                    );
                  })}
              </ul>
            )} */
}
